import React from 'react';
import { Box } from '@mui/material';

type AnimationType = 'scale' | 'rotate' | 'slide';

interface AnimateButtonProps {
  children: React.ReactElement;
  type?: AnimationType;
  direction?: 'up' | 'down' | 'left' | 'right';
  offset?: number;
  scale?: { hover?: number; tap?: number };
}

/**
 * Berry's AnimateButton — wraps any button with a subtle hover animation.
 *
 * type="scale"  → lifts + scales on hover  (default)
 * type="rotate" → slight rotation on hover
 * type="slide"  → translates in a direction on hover
 */
export default function AnimateButton({
  children,
  type = 'scale',
  direction = 'up',
  offset = 4,
}: AnimateButtonProps) {
  const getTranslate = () => {
    switch (direction) {
      case 'up':    return `translateY(-${offset}px)`;
      case 'down':  return `translateY(${offset}px)`;
      case 'left':  return `translateX(-${offset}px)`;
      case 'right': return `translateX(${offset}px)`;
    }
  };

  const hoverSx: object = {
    scale: {
      '& > *': {
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { transform: 'scale(1.02)', boxShadow: '0 4px 20px rgba(94,53,177,0.3)' },
      },
    },
    rotate: {
      '& > *': {
        transition: 'transform 0.2s ease',
        '&:hover': { transform: 'rotate(-3deg) scale(1.02)' },
      },
    },
    slide: {
      '& > *': {
        transition: 'transform 0.2s ease',
        '&:hover': { transform: getTranslate() },
      },
    },
  }[type];

  return <Box sx={hoverSx}>{children}</Box>;
}
